import { useState } from 'react';
import { Button, Switch, Segmented, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import intl from '@/internationalzation/intl';
import { Message } from '@docs/kd-d2c-components';
import { eed_web_icon_global_03_action_guanbizhong_line } from '@src/assets/icons/backup';
import { eed_mario_icon_clock_line } from '@/assets/icons';
import { Icon } from '@docs/kd-d2c-components';
import { getConversationDetailUrl } from '@src/utils/route';
import { kimBridge } from '@src/utils/kim-bridge';
import { isKim, isMobile } from '@/utils/user-agent';
import ConfirmDeleteModal from '../ConfirmDeleteModal/ConfirmDeleteModal';
import SchedulerIcon from '@src/assets/images/scheduler-card-icon.png';
import './AllSchedulerModal.less';

const DEFAULT_NAME = 'mario-all-scheduler-modal';

export interface SchedulerSummaryItem {
  /** 定时任务 ID（后端 config_id） */
  configId: string;
  /** 任务标题 */
  title: string;
  /** 展示用时间文案，例如 “2024/10/12 19:00” 或 “每天 09:00” */
  timeText: string;
  /** 是否启用 */
  enabled: boolean;
}

export interface AllSchedulerModalProps {
  visible: boolean;
  /** 已定时任务列表 */
  scheduledTasks: SchedulerSummaryItem[];
  /** 已完成任务列表 */
  finishedTasks: SchedulerSummaryItem[];
  onClose: () => void;
  /** 点击“新建定时任务”时触发，由外部决定如何打开新增弹窗 */
  onCreateTask: () => void;
  onRunNow: (configId: string) => void;
  onEdit: (configId: string) => void;
  onDelete: (configId: string) => void;
  onToggleEnabled: (configId: string, enabled: boolean) => void;
}

export const AllSchedulerModal = ({
  visible,
  scheduledTasks,
  finishedTasks,
  onClose,
  onCreateTask,
  onRunNow,
  onEdit,
  onDelete,
  onToggleEnabled,
}: AllSchedulerModalProps) => {
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState<'scheduled' | 'finished'>(
    'scheduled',
  );
  const [confirmDeleteConfigId, setConfirmDeleteConfigId] = useState<
    string | null
  >(null);

  const onViewConversation = async (conversationId: string) => {
    if (!conversationId) {
      Message.error({ message: intl.t('会话不存在') });
      return;
    }
    if (isMobile && isKim) {
      await kimBridge.openMario({
        type: 1,
        url: getConversationDetailUrl(conversationId),
      });
      onClose();
      return;
    }
    onClose();
    navigate(getConversationDetailUrl(conversationId, false));
  };

  if (!visible) {
    return null;
  }

  const scheduledColumns: ColumnsType<SchedulerSummaryItem> = [
    {
      title: intl.t('标题'),
      dataIndex: 'title',
      key: 'title',
      width: 276,
      className: `${DEFAULT_NAME}-col-title`,
      render: (text: string) => (
        <div className={`${DEFAULT_NAME}-title-text`}>{text}</div>
      ),
    },
    {
      title: intl.t('计划于'),
      dataIndex: 'timeText',
      key: 'timeText',
      width: 166,
      className: `${DEFAULT_NAME}-col-time`,
      render: (text: string) => {
        const value = text || '';
        if (!value) {
          return null;
        }

        // 一次性任务：将「一次性任务：」放在第一行，完整的「YYYY-MM-DD HH:mm」放在第二行
        if (value.includes('一次性任务') && value.includes('：')) {
          const idx = value.indexOf('：');
          const label = value.slice(0, idx + 1);
          const datetime = value.slice(idx + 1).trim();
          return (
            <div className={`${DEFAULT_NAME}-time-text`}>
              <span className={`${DEFAULT_NAME}-time-label`}>{label}</span>
              <span className={`${DEFAULT_NAME}-time-datetime`}>{datetime}</span>
            </div>
          );
        }

        // 其他情况：直接一行展示
        return (
          <span className={`${DEFAULT_NAME}-time-text`}>
            {value}
          </span>
        );
      },
    },
    {
      title: intl.t('状态'),
      dataIndex: 'enabled',
      key: 'enabled',
      width: 60,
      className: `${DEFAULT_NAME}-col-status`,
      render: (_: boolean, record: SchedulerSummaryItem) => (
        <Switch
          checked={record.enabled}
          size="small"
          onChange={(checked) => onToggleEnabled(record.configId, checked)}
        />
      ),
    },
    {
      title: intl.t('操作'),
      key: 'actions',
      width: 166,
      className: `${DEFAULT_NAME}-col-actions`,
      render: (_: unknown, record: SchedulerSummaryItem) => (
        <>
          <Button
            className={`${DEFAULT_NAME}-run-now-button`}
            type="link"
            style={{ marginRight: '10px', padding: '0px', color: 'var(--color-theme-default)', lineHeight: '18px', fontWeight: '400', fontSize: '14px' }}
            onClick={() => onRunNow(record.configId)
            }
          >
            {intl.t('立即运行')}
          </Button>
          <Button
            className={`${DEFAULT_NAME}-edit-button`}
            type="link"
            style={{ marginRight: '10px', padding: '0px', color: 'var(--color-theme-default)', lineHeight: '18px', fontWeight: '400', fontSize: '14px' }}
            onClick={() => onEdit(record.configId)}
          >
            {intl.t('编辑')}
          </Button>
          <Button
            className={`${DEFAULT_NAME}-delete-button`}
            type="link"
            style={{ padding: '0px', color: 'var(--color-theme-default)', lineHeight: '18px', fontWeight: '400', fontSize: '14px' }}
            onClick={() => setConfirmDeleteConfigId(record.configId)}
          >
            {intl.t('mario.button.delete')}
          </Button>
        </>
      ),
    },
  ];

  const finishedColumns: ColumnsType<SchedulerSummaryItem> = [
    {
      title: intl.t('标题'),
      dataIndex: 'title',
      key: 'title',
      className: `${DEFAULT_NAME}-col-title`,
      width: 502,
      render: (text: string) => (
        <div className={`${DEFAULT_NAME}-title-text`}>{text}</div>
      ),
    },
    {
      title: intl.t('时间'),
      dataIndex: 'timeText',
      key: 'timeText',
      width: 166,
      className: `${DEFAULT_NAME}-col-time`,
    },
  ];

  const handleInnerClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };

  const renderScheduledList = () => {
    if (!scheduledTasks.length) {
      return (
        <span className={`${DEFAULT_NAME}-empty`}>
          {intl.t('当前没有定时任务')}
        </span>
      );
    }

    return (
      <Table<SchedulerSummaryItem>
        className={`${DEFAULT_NAME}-table`}
        columns={scheduledColumns}
        dataSource={scheduledTasks}
        rowKey="configId"
        scroll={{ y: 320 }}
        pagination={{
          pageSize: 20,
          showSizeChanger: false,
          showTotal: (total) => intl.t(`共 ${total} 条`),
          size: 'small',
        }}
        rowClassName={() => `${DEFAULT_NAME}-row`}
      />
    );
  };

  const renderFinishedList = () => {
    if (!finishedTasks.length) {
      return (
        <span className={`${DEFAULT_NAME}-empty`}>
          {intl.t('当前没有已完成的定时任务')}
        </span>
      );
    }
    return (
      <Table<SchedulerSummaryItem>
        className={`${DEFAULT_NAME}-table ${DEFAULT_NAME}-table-finished`}
        columns={finishedColumns}
        dataSource={finishedTasks}
        rowKey="configId"
        scroll={{ y: 320 }}
        pagination={{
          pageSize: 20,
          showSizeChanger: false,
          showTotal: (total) => intl.t(`共 ${total} 条`),
          size: 'small',
        }}
        rowClassName={() => `${DEFAULT_NAME}-row`}
        onRow={(record) => ({
          onClick: () => onViewConversation(record.configId),
        })}
      />
    );
  };

  const handleNewScheduler = () => {
    onCreateTask();
  };

  return (
    <div className={`${DEFAULT_NAME}-mask`}>
      <div className={DEFAULT_NAME} onClick={handleInnerClick}>
        <div className={`${DEFAULT_NAME}-header`}>
          <div className={`${DEFAULT_NAME}-header-left`}>
            <img
              className={`${DEFAULT_NAME}-header-icon`}
              src={SchedulerIcon}
              alt="scheduler-icon"
            />
            <div className={`${DEFAULT_NAME}-title`}>
              {intl.t('所有定时任务')}
            </div>
          </div>
          <div className={`${DEFAULT_NAME}-header-right`}>
            <Icon
              style={{ cursor: 'pointer' }}
              icon={eed_web_icon_global_03_action_guanbizhong_line}
              size={16}
              onClick={onClose}
            />
          </div>
        </div>

        <div className={`${DEFAULT_NAME}-tabs`}>
          <div className={`${DEFAULT_NAME}-tabs-header`}>
            <Segmented
              className={`${DEFAULT_NAME}-tabs-header-segmented`}
              value={activeKey}
              onChange={(val) =>
                setActiveKey((val as 'scheduled' | 'finished') ?? 'scheduled')
              }
              options={[
                { label: intl.t('已定时'), value: 'scheduled' },
                { label: intl.t('已完成'), value: 'finished' },
              ]}
            />
            <Button
              className={`${DEFAULT_NAME}-new-scheduler-button`}
              type="primary"
              size="middle"
              onClick={handleNewScheduler}
            >
              <Icon icon={eed_mario_icon_clock_line} size={16} />
              {intl.t(' 新建定时任务')}
            </Button>
          </div>
          <div className={`${DEFAULT_NAME}-content`}>
            {activeKey === 'scheduled'
              ? renderScheduledList()
              : renderFinishedList()}
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        visible={confirmDeleteConfigId != null}
        onClose={() => setConfirmDeleteConfigId(null)}
        onConfirm={() => {
          if (confirmDeleteConfigId) {
            onDelete(confirmDeleteConfigId);
            setConfirmDeleteConfigId(null);
          }
        }}
      />
    </div>
  );
};

export default AllSchedulerModal;

